import {
    AfterViewInit,
    Component,
    ElementRef,
    OnDestroy,
    OnInit,
    ViewChild,
} from '@angular/core';
import { NgbTabset } from '@cmps/ng-bootstrap';
import { IOption } from '@cmps/ng-select';
import { forkJoin, Subscription } from 'rxjs';
import { finalize, map, take, tap } from 'rxjs/operators';
import { utils, WorkBook, WorkSheet, writeFile } from 'xlsx';

import { AuthService } from '../../auth/auth.service';
import { Util } from '../../common/common.util';
import { ProfileUserService } from '../../user/profile.user.service';
import { TranslationKeyMap } from '../reports.i18n';
import { ReportsService } from '../reports.service';
import { toCamel } from '../td-daily-huddle/td-daily-huddle.reports.component';

import {
    TdBuilderUsageMetricReportPopulationType,
    TdBuilderUsageMetricReportResultParams,
    TdBuilderUsageMetricReportSelectionParams,
    TdBuilderUsageMetricResult,
    TdBuilderUsageMetricResultRow,
} from './td-builder-usage-metric.reports.model';

enum Tabs {
    PARAMS = 'w-tab-report-0',
    GRID = 'w-tab-report-1',
}

@Component({
    selector: '[tdBuilderUsageMetricReport]',
    templateUrl: './td-builder-usage-metric.reports.component.html',
    host: {
        class: 'cmps-reports-td-builder-usage-metric',
    },
})
export class TdBuilderUsageMetricReportComponent
    implements OnInit, AfterViewInit, OnDestroy
{
    @ViewChild('tabSet', { static: true }) tabSet: NgbTabset;
    @ViewChild('table') table: ElementRef<HTMLTableElement>;
    i18n = TranslationKeyMap;
    pendingData: boolean;
    noData: boolean = false;
    reportId: number;
    currentDate = new Date();
    selectedParams: TdBuilderUsageMetricReportSelectionParams = {
        startDate: Util.dateToNgbDate(
            new Date(
                this.currentDate.getFullYear(),
                this.currentDate.getMonth(),
                1,
            ),
        ),
        endDate: Util.dateToNgbDate(this.currentDate),
        populationType: TdBuilderUsageMetricReportPopulationType.Individual,
        populationID: null,
        defaultPopulationID: null,
        noData: false,
        pendingData: false,
        invalidEndDate: false,
        invalidStartDate: false,
    };

    params: TdBuilderUsageMetricReportResultParams;

    sub: Subscription;
    selectedPopulation: string;
    selectedPopulationType: string;
    roles: IOption[];
    peerGroups: IOption[];
    superGroups: IOption[];
    reportPopulations: IOption[] = [
        {
            label: 'Role',
            value: TdBuilderUsageMetricReportPopulationType.Role,
        },
        {
            label: 'SuperGroup',
            value: TdBuilderUsageMetricReportPopulationType.SuperGroup,
        },
        {
            label: 'PeerGroup',
            value: TdBuilderUsageMetricReportPopulationType.PeerGroup,
        },
        {
            label: 'Individual',
            value: TdBuilderUsageMetricReportPopulationType.Individual,
        },
        {
            label: 'Reps Under',
            value: TdBuilderUsageMetricReportPopulationType.RepsUnder,
        },
    ];

    data: TdBuilderUsageMetricResultRow[];

    constructor(
        private readonly _reportService: ReportsService,
        private readonly _profileUserService: ProfileUserService,
        private readonly _authService: AuthService,
    ) {}

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    ngOnInit() {
        this.selectedParams.pendingData = true;
        const user = {
            userId: this._authService.user.id,
            userName: `${this._authService.user.firstName} ${this._authService.user.lastName}`,
        };
        this.selectedParams = {
            ...this.selectedParams,
            populationID: user,
            defaultPopulationID: user,
        };
        forkJoin([
            this._profileUserService.listRoles().pipe(
                take(1),
                map((roles) =>
                    roles.map((p) => ({
                        value: p.id.toString(),
                        label: p.name,
                    })),
                ),
                // eslint-disable-next-line no-return-assign
                tap((roles) => (this.roles = roles)),
            ),
            this._profileUserService.listPeerGroups().pipe(
                take(1),
                map((peerGroups) =>
                    peerGroups
                        .filter((p) => p.enabled)
                        .map((p) => ({
                            value: p.id.toString(),
                            label: p.name,
                        })),
                ),
                // eslint-disable-next-line no-return-assign
                tap((peerGroups) => (this.peerGroups = peerGroups)),
            ),
            this._profileUserService.listSuperGroups().pipe(
                take(1),
                map((superGroups) =>
                    superGroups
                        .filter((s) => s.enabled)
                        .map((s) => ({
                            value: s.id.toString(),
                            label: s.name,
                        })),
                ),
                // eslint-disable-next-line no-return-assign
                tap((superGroups) => (this.superGroups = superGroups)),
            ),
        ])
            .pipe(
                finalize(() => {
                    this.selectedParams.pendingData = false;
                }),
            )
            .subscribe();
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    ngAfterViewInit() {
        this.sub = this.tabSet.tabChange
            .pipe(
                tap((t) => {
                    if (t.nextId === Tabs.GRID) {
                        this.getReportResult();
                    }
                }),
            )
            .subscribe();
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    ngOnDestroy() {
        if (this.sub) {
            this.sub.unsubscribe();
        }
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    getReportResult() {
        if (!this.reportId) {
            this.noData = true;
            return;
        }
        this.pendingData = true;
        this._reportService
            .retrieveGenericReportResult(this.reportId)
            .pipe(
                take(1),
                tap((result) => {
                    if (result.resultSet && result.resultSet.length > 0) {
                        this.params = this.getParams(result);
                        this.getSelectedPopulation();
                        const data = toCamel<TdBuilderUsageMetricResultRow>(
                            JSON.parse(result.resultSet),
                        );
                        this.data = Array.isArray(data) ? data : [];
                        this.noData = false;
                    }
                }),
                // eslint-disable-next-line no-return-assign
                finalize(() => (this.pendingData = false)),
            )
            .subscribe();
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    onGeneratedReport(reportId: number) {
        this.reportId = reportId;
        this.tabSet.select('w-tab-report-1');
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    onExportClick() {
        const workSheet: WorkSheet = utils.table_to_sheet(
            this.table.nativeElement,
        );
        const workBook: WorkBook = utils.book_new();
        utils.book_append_sheet(workBook, workSheet, 'Sheet1');
        writeFile(workBook, 'Report Builder Usage - Metric.xlsx', {
            bookType: 'xlsx',
            type: 'buffer',
        });
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    private getSelectedPopulation() {
        const selected = this.reportPopulations.find(
            (p) =>
                p.label.split(' ').join('') === this.params.populationType ||
                p.value === this.params.populationType,
        );
        this.selectedPopulationType = selected.label;

        switch (selected.value) {
            case TdBuilderUsageMetricReportPopulationType.SuperGroup:
                // eslint-disable-next-line no-case-declarations
                const superGroup = this.superGroups.find(
                    (s) => s.value === this.params.populationID,
                );
                this.selectedPopulation = superGroup ? superGroup.label : '';
                break;
            case TdBuilderUsageMetricReportPopulationType.PeerGroup:
                // eslint-disable-next-line no-case-declarations
                const peerGroup = this.peerGroups.find(
                    (s) => s.value === this.params.populationID,
                );
                this.selectedPopulation = peerGroup ? peerGroup.label : '';
                break;
            case TdBuilderUsageMetricReportPopulationType.Role:
                // eslint-disable-next-line no-case-declarations
                const role = this.roles.find(
                    (s) => s.value === this.params.populationID,
                );
                this.selectedPopulation = role ? role.label : '';
                break;
            case TdBuilderUsageMetricReportPopulationType.Individual:
            case TdBuilderUsageMetricReportPopulationType.RepsUnder:
                this._reportService
                    .retrieveUsersNameById(this.params.populationID)
                    .pipe(
                        take(1),
                        tap((r) => {
                            if (r.length > 0) {
                                this.selectedPopulation = r[0].userName;
                            }
                        }),
                    )
                    .subscribe();
                break;
            default:
                break;
        }
    }

    private getParams(
        result: TdBuilderUsageMetricResult,
    ): TdBuilderUsageMetricReportResultParams {
        return result.params.split('@').reduce((output, current) => {
            const keyValue = current.split('=').map((v) => v.trim());
            const key = (
                keyValue[0].charAt(0).toLowerCase() + keyValue[0].slice(1) ||
                keyValue[0]
            ).toString();
            const value = keyValue[1];
            if (key) {
                output[key] = value.replace(/^,+|,+$/g, '');
            }
            return output;
        }, {}) as TdBuilderUsageMetricReportResultParams;
    }
}
